import { Box, LinearProgress, Typography, Grid, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

const UploadProgressSnackbar = ({ progressRefs, attachments: initialAttachments = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progressValues, setProgressValues] = useState([]);
  const [attachments, setAttachments] = useState(initialAttachments);

  useEffect(() => {
    if (attachments.length !== initialAttachments.length) {
      setAttachments(initialAttachments);
      setProgressValues(Array(initialAttachments.length).fill(0));
      setCurrentIndex(0);
    }
  }, [initialAttachments, attachments.length]);

  useEffect(() => {
    if (attachments.length === 0) return;
    progressRefs.current = attachments.map((_, i) => (value) => {
      setProgressValues((prev) => {
        const updated = [...prev];
        updated[i] = value;

        if (value >= 100) {
          setAttachments((prevAttachments) => {
            const newAttachments = prevAttachments.filter((_, idx) => idx !== i);

            if (newAttachments.length === 0) {
              setProgressValues([]);
              setCurrentIndex(0);
              progressRefs.current = [];
            } else if (currentIndex >= newAttachments.length) {
              setCurrentIndex(Math.max(0, newAttachments.length - 1));
            }

            return newAttachments;
          });
        }
        return updated;
      });
    });
  }, [attachments, currentIndex, progressRefs]);

  useEffect(() => {
    if (attachments.length > 0) {
      const handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = "File upload progress will be lost. Are you sure you want to leave?";
        return e.returnValue;
      };
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  }, [attachments]);

  const handleNext = () => {
    if (currentIndex < attachments.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const handleBack = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };


  return (
    progressRefs?.current?.length === 0 ? (
      <Box sx={{ width: 350, p: 2 }}>
        <Typography variant="body2" sx={{ textAlign: "center"}}>
          All files uploaded successfully. No files in upload progress.
        </Typography>
      </Box>
    ) : (
      <Box sx={{ width: 350, p: 1 }}>
        <Grid container alignItems="center">
          <Grid size={{ md: 6, xs: 12 }}>File Upload Progress</Grid>
          {attachments.length > 1 && (
            <>
              <Grid size={{ md: 1, xs: 12 }}></Grid>
              <Grid size={{ md: 5, xs: 12 }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                  <Tooltip title="View previous file">
                    <ArrowBackIosIcon
                      fontSize="small"
                      sx={{
                        cursor: currentIndex === 0 ? "not-allowed" : "pointer",
                        height: 15,
                        width: 15,
                      }}
                      onClick={handleBack}
                    />
                  </Tooltip>
                  <Typography variant="caption" sx={{ mt: "-2px" }}>
                    {currentIndex + 1} / {attachments.length}
                  </Typography>
                  <Tooltip title="View next file">
                    <ArrowForwardIosIcon
                      fontSize="small"
                      sx={{
                        cursor: currentIndex === attachments.length - 1 ? "not-allowed" : "pointer",
                        height: 15,
                        width: 15,
                      }}
                      onClick={handleNext}
                    />
                  </Tooltip>
                </Box>
              </Grid>
            </>
          )}
        </Grid>
        <Typography variant="caption" sx={{ mb: 1 }}>
          {attachments[currentIndex]?.name} — {progressValues[currentIndex]}%
        </Typography>
        <LinearProgress variant="determinate" value={progressValues[currentIndex]} />
      </Box>
    )
  );
};

export default UploadProgressSnackbar;
